<!-- 宠物统计/排行榜下钻：按筛选条件分页查看学生，支持姓名学号搜索与查看成长详情。创建于 2026-10-08 -->
<template>
  <div>
    <el-dialog
      :title="title"
      :visible="visible"
      width="1000px"
      top="5vh"
      append-to-body
      custom-class="pet-student-list-dialog"
      @close="$emit('update:visible', false)">
      <div class="toolbar">
        <div class="toolbar__tags">
          <span v-for="tag in tags" :key="tag" class="filter-tag">{{ tag }}</span>
        </div>
        <div class="toolbar__search">
          <el-input
            v-model="keyword"
            size="small"
            clearable
            placeholder="搜索姓名 / 学号"
            prefix-icon="el-icon-search"
            class="search-input"
            @keyup.enter.native="search"
            @clear="search" />
          <el-button type="primary" size="small" @click="search">查询</el-button>
        </div>
      </div>

      <el-table v-loading="loading" :data="rows" size="small" height="min(460px, calc(88vh - 240px))" class="student-table">
        <el-table-column label="学生" min-width="200">
          <template slot-scope="{ row }">
            <div class="who">
              <span class="who__face">
                <oss-media-thumbs v-if="row.headPhotoImage" :media="row.headPhotoImage" :max="1" thumb-width="32px" thumb-height="32px" />
                <span v-else class="who__initial">{{ (row.studentName || '?').slice(-1) }}</span>
              </span>
              <span class="who__text">
                <span class="who__name" :title="row.studentName">{{ row.studentName }}</span>
                <span class="who__sub">{{ row.studentNo || '—' }}</span>
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="departmentName" label="班级" min-width="120" align="center" show-overflow-tooltip />
        <el-table-column label="当前宠物" min-width="150" align="center">
          <template slot-scope="{ row }">
            <div v-if="row.hasPet" class="pet-cell">
              <span class="pet-cell__face">
                <oss-media-thumbs v-if="row.avatarUrl" :media="row.avatarUrl" :max="1" thumb-width="26px" thumb-height="26px" />
              </span>
              <span class="pet-cell__name" :title="row.petName">{{ row.petName || '已删除宠物' }}</span>
            </div>
            <span v-else class="muted">未领取</span>
          </template>
        </el-table-column>
        <el-table-column label="等级" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span class="level-badge">Lv.{{ row.levelIndex || 1 }}</span>
            <span class="level-name">{{ row.levelName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="学期分" min-width="90" align="center">
          <template slot-scope="{ row }"><b class="num">{{ formatScore(row.semesterScore) }}</b></template>
        </el-table-column>
        <el-table-column label="当日得分" min-width="90" align="center">
          <template slot-scope="{ row }"><span :class="['num', scoreClass(row.todayScore)]">{{ signedScore(row.todayScore) }}</span></template>
        </el-table-column>
        <el-table-column label="当日状态" min-width="90" align="center">
          <template slot-scope="{ row }"><span :class="['mood-tag', 'mood-' + moodKey(row.mood)]">{{ row.moodText || '平稳' }}</span></template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          @current-change="handlePageChange"
          @size-change="handleSizeChange" />
      </div>
    </el-dialog>

    <student-pet-detail-drawer
      :visible.sync="detailVisible"
      :school-id="schoolId"
      :student-id="detailStudentId"
      :semester-id="filters.semesterId || null"
      :semesters="semesters" />
  </div>
</template>

<script>
import StudentPetDetailDrawer from './StudentPetDetailDrawer.vue'
import { getPetStudentList } from '@/api/modules/pet'

export default {
  name: 'PetStudentListDialog',
  components: { StudentPetDetailDrawer },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '学生明细' },
    schoolId: { type: Number, default: 0 },
    /** 下钻筛选：departmentId、semesterId、viewDate、claimStatus、levelIndex、petId、moodCode、activeOnly */
    filters: { type: Object, default: () => ({}) },
    /** 顶部展示的筛选说明 */
    tags: { type: Array, default: () => [] },
    semesters: { type: Array, default: () => [] }
  },
  data () {
    return {
      loading: false,
      keyword: '',
      rows: [],
      total: 0,
      page: 1,
      pageSize: 10,
      detailVisible: false,
      detailStudentId: 0
    }
  },
  watch: {
    visible (val) {
      if (!val) return
      this.keyword = ''
      this.page = 1
      this.rows = []
      this.total = 0
      this.load()
    }
  },
  methods: {
    search () {
      this.page = 1
      this.load()
    },
    handlePageChange (page) {
      this.page = page
      this.load()
    },
    handleSizeChange (size) {
      this.pageSize = size
      this.page = 1
      this.load()
    },
    /** 按下钻条件 + 关键字分页查询学生 */
    async load () {
      if (!this.schoolId) return
      this.loading = true
      try {
        const params = Object.assign({}, this.filters, {
          schoolId: this.schoolId,
          keyword: this.keyword.trim() || null,
          page: this.page,
          pageSize: this.pageSize
        })
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') delete params[key]
        })
        const res = await getPetStudentList(params)
        if (res.data && res.data.success) {
          const data = res.data.response || {}
          const rows = data.data || []
          await this.prefetchListOssMedia(rows, ['headPhotoImage', 'avatarUrl'])
          this.rows = rows
          this.total = data.dataCount || 0
        } else {
          this.$message.error((res.data && res.data.msg) || '加载失败')
        }
      } finally {
        this.loading = false
      }
    },
    openDetail (row) {
      this.detailStudentId = row.studentId
      this.detailVisible = true
    },
    moodKey (mood) {
      const value = String(mood || '').toLowerCase()
      if (value === 'energetic') return 'energetic'
      if (value === 'adjusting') return 'adjusting'
      return 'stable'
    },
    formatScore (value) {
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    scoreClass (value) {
      const num = Number(value) || 0
      return num > 0 ? 'is-up' : num < 0 ? 'is-down' : ''
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.toolbar__tags { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; min-width: 0; }
.filter-tag { padding: 2px 10px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; }
.toolbar__search { display: flex; align-items: center; gap: 8px; }
.search-input { width: 220px; }
.student-table { width: 100%; }
.student-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }
.who { display: flex; align-items: center; gap: 10px; min-width: 0; }
.who__face {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.who__face /deep/ .oss-media-thumbs {
  width: 32px;
  height: 32px;
  display: block;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: 0;
}
.who__face /deep/ .oss-media-thumbs-more { display: none !important; }
.who__face /deep/ .oss-media-thumbs img {
  width: 32px !important;
  height: 32px !important;
  border-radius: 6px;
  object-fit: cover;
  display: block;
}
.who__initial {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
}
.who__text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;
}
.who__name {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.who__sub { font-size: 12px; color: #64748b; }
.pet-cell { display: inline-flex; align-items: center; gap: 8px; max-width: 100%; min-width: 0; }
.pet-cell__face {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  position: relative;
  overflow: hidden;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pet-cell__face /deep/ .oss-media-thumbs {
  width: 26px;
  height: 26px;
  display: block;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: 0;
}
.pet-cell__face /deep/ .oss-media-thumbs-more { display: none !important; }
.pet-cell__face /deep/ .oss-media-thumbs img {
  width: 26px !important;
  height: 26px !important;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.pet-cell__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
}
.level-badge { display: inline-block; padding: 1px 7px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 600; }
.level-name { margin-left: 4px; font-size: 12px; color: #475569; }
.num { font-variant-numeric: tabular-nums; color: #0f172a; }
.num.is-up { color: #15803d; }
.num.is-down { color: #b45309; }
.muted { color: #94a3b8; font-size: 12px; }
.mood-tag { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.mood-stable { background: #eff6ff; color: #1d4ed8; }
.mood-energetic { background: #dcfce7; color: #15803d; }
.mood-adjusting { background: #fef3c7; color: #b45309; }
.pager { display: flex; justify-content: flex-end; margin-top: 12px; }
</style>

<style>
.el-dialog.pet-student-list-dialog > .el-dialog__body { max-height: none !important; overflow: hidden !important; }
</style>
